import React from "react";
import { motion } from "framer-motion";
import { Lock, Clock, ShieldCheck, Ban, ChevronRight } from "lucide-react";
import { MagneticButton, RevealOnScroll, scrollToId } from "../../marketingAgency/shared";
import {
  TERRITORY_CTA,
  TERRITORY_DETAILS,
  TERRITORY_FOOTNOTE,
  TERRITORY_LOCK,
  TERRITORY_LOCK_THESIS,
  TERRITORY_SLOT_BLOCKED,
  TERRITORY_SLOT_EXAMPLE,
  TERRITORY_SLOT_LOCKED,
  TERRITORY_STATUS_BADGE,
  TERRITORY_STEPS,
  TERRITORY_WAIT,
} from "./territoryLockContent";

const StatusBadge = () => (
  <div
    className="inline-flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-xl border border-amber-500/35 bg-amber-500/10 px-4 py-3 mb-6"
    data-testid="territory-status-badge"
  >
    <div className="flex items-center gap-2">
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400" />
      </span>
      <span className="font-mono text-xs sm:text-sm font-semibold text-amber-200 tracking-wide">
        {TERRITORY_STATUS_BADGE.status}
      </span>
    </div>
    <span className="hidden sm:block text-amber-500/50 font-mono text-xs">|</span>
    <span className="font-mono text-xs sm:text-sm text-amber-100/90">{TERRITORY_STATUS_BADGE.slot}</span>
  </div>
);

const SlotCard = ({ variant, slot, owner, status, caption, delay = 0 }) => {
  const isLocked = variant === "locked";

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.35, delay }}
      className={`rounded-xl border p-5 sm:p-6 flex flex-col ${
        isLocked
          ? "border-[#00D2FF]/35 bg-[#00D2FF]/5"
          : "border-red-500/25 bg-red-500/5 opacity-90"
      }`}
      data-testid={`territory-slot-${variant}`}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-mono text-partner-muted uppercase tracking-wider mb-1">Example slot</p>
          <p className="text-lg font-bold text-partner leading-tight">
            {slot.niche} · {slot.city}
          </p>
        </div>
        <div
          className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
            isLocked
              ? "border-[#00D2FF]/35 bg-[#00D2FF]/10"
              : "border-red-500/30 bg-red-500/10"
          }`}
        >
          {isLocked ? (
            <ShieldCheck className="w-5 h-5 text-partner-accent" />
          ) : (
            <Ban className="w-5 h-5 text-red-400" />
          )}
        </div>
      </div>

      <div
        className={`rounded-lg border px-4 py-3 mb-3 ${
          isLocked ? "border-[#00D2FF]/25 bg-[#080A0F]" : "border-red-500/20 bg-[#080A0F]"
        }`}
      >
        <p className="font-mono text-[10px] text-partner-muted tracking-widest mb-1">{owner}</p>
        <p
          className={`font-mono text-xl sm:text-2xl font-bold ${
            isLocked ? "text-partner-accent" : "text-red-400"
          }`}
        >
          {status}
        </p>
      </div>

      <p className={`text-sm font-body ${isLocked ? "text-partner-muted" : "text-red-300/70"}`}>{caption}</p>
    </motion.div>
  );
};

const StepTimeline = () => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-testid="territory-step-timeline">
    {TERRITORY_STEPS.map((step, index) => (
      <div key={step.id} className="flex sm:flex-col sm:items-center sm:text-center gap-3">
        <div className="w-9 h-9 rounded-full border border-[#00D2FF]/40 bg-[#00D2FF]/10 flex items-center justify-center shrink-0">
          <span className="font-mono text-xs font-bold text-partner-accent">{index + 1}</span>
        </div>
        <div className="min-w-0">
          <p className="font-semibold text-partner text-sm">{step.label}</p>
          <p className="text-xs text-partner-muted font-body mt-0.5">{step.detail}</p>
        </div>
      </div>
    ))}
  </div>
);

const ChoiceCard = ({ block, variant, delay = 0 }) => {
  const isLock = variant === "lock";
  const Icon = isLock ? Lock : Clock;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-30px" }}
      transition={{ duration: 0.35, delay }}
      className={`rounded-xl border p-5 sm:p-6 ${
        isLock ? "border-[#00D2FF]/30 bg-[#00D2FF]/5" : "border-red-500/25 bg-red-500/5"
      }`}
      data-testid={`territory-choice-${variant}`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg border flex items-center justify-center shrink-0 ${
            isLock
              ? "border-[#00D2FF]/35 bg-[#00D2FF]/10"
              : "border-red-500/30 bg-red-500/10"
          }`}
        >
          <Icon className={`w-5 h-5 ${isLock ? "text-partner-accent" : "text-red-400"}`} />
        </div>
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-partner-muted mb-1">{block.title}</p>
          <p className="text-base font-bold text-partner mb-1">{block.headline}</p>
          <p className="text-sm text-partner-muted font-body leading-relaxed">{block.sub}</p>
        </div>
      </div>
    </motion.div>
  );
};

const TerritoryLockSection = ({ id = "territory-lock" }) => (
  <section
    id={id}
    className="section-padding section-alt relative overflow-hidden"
    data-testid="territory-lock"
  >
    <div className="max-w-6xl mx-auto px-6 lg:px-8">
      <RevealOnScroll>
        <StatusBadge />
        <h2 className="text-3xl sm:text-4xl font-bold text-partner leading-tight mb-3 max-w-3xl">
          {TERRITORY_LOCK_THESIS.headline}
        </h2>
        <p className="text-partner-muted font-body max-w-2xl leading-relaxed mb-10">
          {TERRITORY_LOCK_THESIS.subline}
        </p>
      </RevealOnScroll>

      <RevealOnScroll delay={0.06}>
        <div
          className="rounded-2xl border border-partner bg-[#080A0F] p-5 sm:p-8 mb-8"
          data-testid="territory-slot-visual"
        >
          <p className="text-[10px] font-mono text-partner-accent tracking-widest mb-5 uppercase">
            First to deploy wins the slot
          </p>
          <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-8">
            <SlotCard
              variant="locked"
              slot={TERRITORY_SLOT_EXAMPLE}
              owner={TERRITORY_SLOT_LOCKED.owner}
              status={TERRITORY_SLOT_LOCKED.status}
              caption={TERRITORY_SLOT_LOCKED.caption}
              delay={0}
            />
            <SlotCard
              variant="blocked"
              slot={TERRITORY_SLOT_EXAMPLE}
              owner={TERRITORY_SLOT_BLOCKED.owner}
              status={TERRITORY_SLOT_BLOCKED.status}
              caption={TERRITORY_SLOT_BLOCKED.caption}
              delay={0.08}
            />
          </div>
          <StepTimeline />
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.1}>
        <div className="grid md:grid-cols-2 gap-4 sm:gap-6 mb-8">
          <ChoiceCard block={TERRITORY_LOCK} variant="lock" delay={0} />
          <ChoiceCard block={TERRITORY_WAIT} variant="wait" delay={0.06} />
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.12}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-6">
          <MagneticButton
            className="px-8 py-3.5 rounded-2xl font-semibold text-base inline-flex items-center gap-2 shrink-0"
            onClick={() => scrollToId(TERRITORY_CTA.target)}
            data-testid="territory-claim-cta"
          >
            <span>{TERRITORY_CTA.label}</span>
            <ChevronRight className="w-5 h-5" />
          </MagneticButton>
          <p className="text-sm text-partner-muted font-body">
            One slot per niche per city. Enrollment is first-come, first-served.
          </p>
        </div>
      </RevealOnScroll>

      <RevealOnScroll delay={0.14}>
        <details className="group rounded-xl border border-partner bg-[#11151F] mb-4">
          <summary className="cursor-pointer list-none px-5 py-4 text-sm font-semibold text-partner flex items-center justify-between gap-3 [&::-webkit-details-marker]:hidden">
            {TERRITORY_DETAILS.summary}
            <ChevronRight className="w-4 h-4 text-partner-muted transition-transform group-open:rotate-90 shrink-0" />
          </summary>
          <div className="px-5 pb-5 pt-0 border-t border-partner">
            <p className="text-sm text-partner-muted font-body leading-relaxed pt-4">{TERRITORY_DETAILS.body}</p>
          </div>
        </details>
        <p className="text-[11px] font-mono text-partner-muted">{TERRITORY_FOOTNOTE}</p>
      </RevealOnScroll>
    </div>
  </section>
);

export default TerritoryLockSection;
